import { config } from "../config";
import type { RootState } from "../store/store";
import { applyRioAssistDarkMode } from "./rioAssistDarkMode";
import { applyFloatingPosition, cleanupFloatingPosition } from "./rioAssistFloatingPosition";
import { applyWidgetUiOverrides, cleanupWidgetUiOverridesIfAny } from "./rioAssistUiOverrides";

declare global {
  interface Window {
    RioAssist?: {
      init: (options?: { rioToken?: string; [key: string]: unknown }) => void;
    };
  }
}

const asBool = (value: string | undefined): boolean => value === "true";

const WIDGET_STRING_OPTIONS: Record<string, string | undefined> = {
  title: process.env.REACT_APP_RIO_ASSIST_TITLE,
  buttonLabel: process.env.REACT_APP_RIO_ASSIST_BUTTON_LABEL,
  placeholder: process.env.REACT_APP_RIO_ASSIST_PLACEHOLDER,
  accentColor: process.env.REACT_APP_RIO_ASSIST_ACCENT_COLOR,
  floatingButtonIconUrl: process.env.REACT_APP_RIO_ASSIST_FLOATING_BUTTON_ICON_URL,
  floatingButtonLabelIconUrl: process.env.REACT_APP_RIO_ASSIST_FLOATING_BUTTON_LABEL_ICON_URL,
  floatingButtonBackgroundIconUrl: process.env.REACT_APP_RIO_ASSIST_FLOATING_BUTTON_BACKGROUND_ICON_URL,
  consultantAgentButtonText: process.env.REACT_APP_RIO_ASSIST_CONSULTANT_AGENT_BUTTON_TEXT,
  consultantAgentInitialMessage: process.env.REACT_APP_RIO_ASSIST_CONSULTANT_AGENT_INITIAL_MESSAGE,
};

const WIDGET_BOOLEAN_OPTIONS: Record<string, string | undefined> = {
  showConsultantAgentButton: process.env.REACT_APP_RIO_ASSIST_SHOW_CONSULTANT_AGENT_BUTTON,
  autoStartConsultantFlow: process.env.REACT_APP_RIO_ASSIST_AUTO_START_CONSULTANT_FLOW,
};

const buildWidgetOptions = (token: string): Record<string, unknown> => {
  const options: Record<string, unknown> = { rioToken: token };

  if (config.homeRoute) {
    options.homeUrl = config.homeRoute;
  }

  const suggestions = process.env.REACT_APP_RIO_ASSIST_SUGGESTIONS?.split("|")
    .map((entry) => entry.trim())
    .filter(Boolean);
  if (suggestions?.length) {
    options.suggestions = suggestions;
  }

  for (const [key, value] of Object.entries(WIDGET_STRING_OPTIONS)) {
    if (value) {
      options[key] = value;
    }
  }

  for (const [key, value] of Object.entries(WIDGET_BOOLEAN_OPTIONS)) {
    if (value) {
      options[key] = asBool(value);
    }
  }

  return options;
};

let lastToken: string | null = null;
let cleanupDarkMode: (() => void) | null = null;

const ensureHostStyles = () => {
  const styleId = "rio-assist-host-style";
  if (document.getElementById(styleId)) return;

  const style = document.createElement("style");
  style.id = styleId;
  style.textContent = `
    rio-assist-widget {
      position: fixed;
      inset: 0;
      pointer-events: none;
      z-index: 2147483646;
    }
    rio-assist-widget .floating-button,
    rio-assist-widget .panel,
    rio-assist-widget .fullscreen-shell {
      pointer-events: auto;
    }
  `;
  document.head.appendChild(style);
};

const ensureHost = (): HTMLElement => {
  let host = document.querySelector("rio-assist-widget") as HTMLElement | null;
  if (!host) {
    host = document.createElement("rio-assist-widget");
    document.body.appendChild(host);
  }
  return host;
};

const teardown = () => {
  const host = document.querySelector("rio-assist-widget");
  host?.remove();
  cleanupWidgetUiOverridesIfAny();
  cleanupFloatingPosition();
  cleanupDarkMode?.();
  cleanupDarkMode = null;
  lastToken = null;
};

const initialize = (token: string) => {
  ensureHostStyles();
  const host = ensureHost();
  applyWidgetUiOverrides(host);
  cleanupDarkMode?.();
  cleanupDarkMode = applyRioAssistDarkMode(host);

  const boot = () => {
    applyFloatingPosition(host);
    window.RioAssist?.init(buildWidgetOptions(token));
  };

  if (window.RioAssist) {
    boot();
  } else {
    window.addEventListener("rio-assist-ready", boot, { once: true });
  }
};

export const setupRioAssistWidget = (token: string | null | undefined, isLoggedIn: boolean) => {
  const normalizedToken = token?.trim();
  const ready = isLoggedIn && normalizedToken && normalizedToken !== "NO_ACCESS_TOKEN_AVAILABLE";

  if (!ready) {
    if (lastToken !== null || document.querySelector("rio-assist-widget")) {
      teardown();
    }
    return;
  }

  if (lastToken === normalizedToken) {
    return;
  }

  lastToken = normalizedToken;
  initialize(normalizedToken);
};

export const attachRioAssistToStore = (getState: () => RootState) => {
  const state = getState();
  const manualToken = process.env.REACT_APP_RIO_ASSIST_TOKEN?.trim();
  const token = manualToken || state.tokenHandling?.accessToken;
  const isLoggedIn = Boolean(state.login?.userProfile) && state.login?.userSessionExpired === false;

  setupRioAssistWidget(token, isLoggedIn);
};
